import './cards.css'

import {
  CheckCircleFilled,
  CloseOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useTerminalCards } from '@api/useTerminalCards'
import { BulkChangeBar } from '@components/Admin/Location/Cards/components/BulkChangeEditor/BulkChangeBar'
import { CreateCardFooter } from '@components/Admin/Location/Cards/components/CreateCard/CreateCardFooter'
import { CreateCardForm } from '@components/Admin/Location/Cards/components/CreateCard/CreateCardForm'
import { TerminalSelection } from '@components/Admin/Location/Cards/components/CreateCard/TerminalSelection/TerminalSelection'
import {
  SelectedTerminalProvider,
  useSelectedTerminalContext,
} from '@components/Admin/Location/Cards/components/CreateCard/TerminalSelection/TerminalSelectionContext'
import { ResultsModal } from '@components/Admin/Location/Cards/components/ResultsModal/ResultsModal'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { GraviButton, GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useQueryClient } from '@tanstack/react-query'
import { Drawer, Form, Tooltip, notification } from 'antd'
import { useCallback, useMemo, useRef, useState } from 'react'

import { useSecurityContext } from '@/components/Navigation/SecurityContext'
import { gridViewManager } from '@/utils/grid'
import { getColumnDefs } from './components/getColumnDefs'

const STORAGE_KEY = 'terminal-cards'

export function Cards() {
  return (
    <SelectedTerminalProvider>
      <TerminalCards />
    </SelectedTerminalProvider>
  )
}
export function TerminalCards() {
  const [isCreateDrawerVisible, setIsCreateDrawerVisible] = useState(false)
  const [isBulkChangeVisible, setIsBulkChangeVisible] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [selectedRows, setSelectedRows] = useState([])
  const [resultsData, setResultsData] = useState(null)
  const [createForm] = Form.useForm()
  const { setSelectedTerminals } = useSelectedTerminalContext()
  const { updateSetting, getSetting } = useUserSettings()
  const { scopes } = useSecurityContext()
  const canEdit = !!scopes.admin?.location_write
  const gridAPIRef = useRef(null)
  const getRowId = useCallback((row) => row.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
      rowSelection: 'multiple',
      suppressRowClickSelection: true,
    }),
    [getRowId]
  )
  const { getTerminalCardsOverviewQuery, deleteTerminalCardMutation, updateCardMutation } = useTerminalCards()
  const { data, isFetching, isPending } = getTerminalCardsOverviewQuery()

  const handleResult = (results, action) => {
    if (results?.errors?.length > 0) {
      setResultsData(results)
      return
    }
    if (results?.success > 0) {
      notification.open({
        message: `Successfully ${action}`,
        description: `${results?.success} ${results?.success > 1 ? 'cards' : 'card'} ${action}`,
        icon: <CheckCircleFilled style={{ color: 'var(--theme-success)' }} />,
      })
    }
    setSelectedProperty(null)
  }

  const onBulkChangeBarClosed = (results = null) => {
    handleResult(results, 'updated')
    setIsBulkChangeVisible(false)
    gridAPIRef.current.deselectAll()
    setSelectedRows([])
  }
  const onCreateDrawerClosed = (results = null) => {
    handleResult(results, 'added')
    setIsCreateDrawerVisible(false)
    createForm.resetFields()
    setSelectedTerminals([])
  }

  const columnDefs = useMemo(
    () =>
      getColumnDefs(
        deleteTerminalCardMutation.mutateAsync,
        selectedRows,
        selectedProperty,
        isBulkChangeVisible,
        canEdit
      ),
    [data, selectedRows, selectedProperty, isBulkChangeVisible, canEdit]
  )

  const excelDownloadOptions = useMemo(
    () => ({
      sheetName: 'Terminal Cards',
      fileName: 'Terminal Cards.xlsx',
      allColumns: false,
      columnKeys: [
        'terminal',
        'name',
        'activity',
        'certification_trigger',
        'days_valid',
        'warning_threshold',
        'dispatch_impact',
        'carding_instructions',
        'activity_threshold',
        'market',
        'supply_zone',
      ],
    }),
    []
  )

  const queryClient = useQueryClient()

  const handleUpsert = async (values) => {
    for (const key in values) {
      if (values[key] === '') {
        values[key] = null
      }
    }

    try {
      await updateCardMutation.mutateAsync(values)
    } catch (e) {
      await queryClient.invalidateQueries({
        queryKey: ['terminalCardsOverviewQuery'],
      })
    }
  }

  return (
    <Vertical>
      <GraviGrid
        isBulkChangeVisible={isBulkChangeVisible}
        columnDefs={columnDefs}
        externalRef={gridAPIRef}
        loading={isFetching || isPending}
        rowData={data || []}
        agPropOverrides={agPropOverrides}
        updateEP={handleUpsert}
        controlBarProps={{
          title: (
            <Horizontal verticalCenter>
              <Texto className='mr-2'>Terminal Cards</Texto>
              <Tooltip trigger='hover' title='View assigned cards and add carding instructions'>
                <InfoCircleOutlined style={{ fontSize: '12px' }} />
              </Tooltip>
            </Horizontal>
          ),
          actionButtons: (
            <Horizontal verticalCenter justifyContent='space-evenly'>
              <UploadDownloadButtons
                excelOptions={[
                  {
                    title: 'Download Terminal Cards',
                    onClick: () => gridAPIRef.current.exportDataAsExcel(excelDownloadOptions),
                  },
                ]}
              />
              {canEdit && (
                <>
                  <GraviButton
                    className='mx-2'
                    icon={<EditOutlined />}
                    onClick={() => (isBulkChangeVisible ? onBulkChangeBarClosed() : setIsBulkChangeVisible(true))}
                    buttonText='Bulk Edit'
                    disabled={isCreateDrawerVisible}
                    theme2
                    appearance='outlined'
                  />
                  <GraviButton
                    icon={<PlusOutlined />}
                    onClick={() => {
                      isBulkChangeVisible && onBulkChangeBarClosed()
                      setIsCreateDrawerVisible(true)
                    }}
                    buttonText='Add Card'
                    theme2
                  />
                </>
              )}
            </Horizontal>
          ),
        }}
        onSelectionChanged={(props) => {
          setSelectedRows(props.api.getSelectedRows())
        }}
        storageKey={STORAGE_KEY}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
      {isBulkChangeVisible && (
        <BulkChangeBar
          selectedRows={selectedRows}
          onBulkChangeBarClosed={onBulkChangeBarClosed}
          selectedProperty={selectedProperty}
          setSelectedProperty={setSelectedProperty}
        />
      )}
      {resultsData && (
        <ResultsModal
          resultsData={resultsData}
          setResultsData={setResultsData}
          selectedProperty={selectedProperty}
          setSelectedProperty={setSelectedProperty}
        />
      )}
      <Drawer
        className='create-card-drawer'
        title={
          <Horizontal verticalCenter style={{ width: '100%' }} justifyContent='space-between' className='border-bottom'>
            <Horizontal className='pb-2' gap={8} verticalCenter>
              <Tooltip title='Card variables can be updated for selected terminals'>
                <PlusCircleOutlined />
              </Tooltip>
              <Texto category='h5' className='ml-1'>
                New Terminal Card
              </Texto>
            </Horizontal>
            <GraviButton
              onClick={() => onCreateDrawerClosed()}
              className='ghost-gravi-button'
              icon={<CloseOutlined />}
            />
          </Horizontal>
        }
        closable={false}
        placement='right'
        width={500}
        destroyOnClose
        onClose={() => onCreateDrawerClosed()}
        open={isCreateDrawerVisible}
        footer={<CreateCardFooter form={createForm} onCancel={() => onCreateDrawerClosed()} />}
      >
        <CreateCardForm form={createForm} onClose={onCreateDrawerClosed} />
        <TerminalSelection />
      </Drawer>
    </Vertical>
  )
}
